import React, { useRef } from "react";
import { useEffect } from "react";
import { useMap } from "../../hooks";
import OlTileLayer from "ol/layer/Tile";
import { TileUrl } from "../../utils/utils";
import { XYZ } from "ol/source";
import { unByKey } from "ol/Observable";
import Tile from "ol/Tile";
import ImageTile from "ol/ImageTile";

export type TileMatrix = {
  [zoomLevel: number]: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
};

/**
 * XYZ/TMS 타일 레이어 props — `MapContainer` 자식으로 사용.
 *
 * `url` 패턴은 `{z}/{x}/{y}` placeholder 사용. zoom 별 타일 범위 제한은 `tileMatrix`.
 */
export interface TileLayerProps {
  url: string;

  /**
   * @default 0
   */
  zIndex?: number;

  /**
   * @default 42
   */
  maxZoom?: number;

  /**
   * @default 0
   */
  minZoom?: number;

  /**
   * 타일 이미지의 `crossOrigin` 속성.
   *
   * 기본값이 `"anonymous"` 인 이유: 이 값이 없으면 타일이 레이어 캔버스를 tainted 시켜
   * `toDataURL()`/`toBlob()` 이 `SecurityError` 로 실패한다 — 지도 캡쳐가 성립하지 않는다.
   *
   * ⚠️ 타일 오리진이 `Access-Control-Allow-Origin` 을 응답하지 않으면 **이미지 로드 자체가
   * 실패**해 지도가 보이지 않는다. 새 오리진을 붙일 때는 ACAO 응답을 실측하고, 열려 있지
   * 않다면 그 소비자에 한해 `crossOrigin={null}` 을 명시한다.
   *
   * 현재 소비 중인 오리진은 전부 ACAO 개방 실측 완료(2026-07-27):
   * `s3_endpoint`(정사영상·DSM) · `cloudfront_not_empty_file_url`(tiff GIS) · `api.vworld.kr`(위성).
   *
   * @default "anonymous"
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
   */
  crossOrigin?: null | string | undefined;

  /**
   * 로드에 실패한 타일을 대체할 이미지 URL. 지정 시 404/네트워크 오류 타일을
   * 깨진 이미지 대신 이 이미지로 표시한다(미지정 시 OL 기본 로딩 유지).
   */
  errorTileUrl?: string;
  /**
   * 레이어 불투명도 — **OL 규약(1 = 불투명, 0 = 완전 투명)**.
   *
   * UI 의 "투명도"(0 = 불투명)와 반대 축이다 — 변환은 소비처가 끝내고 넘긴다
   * (유일 지점: `useTmsLayerTransparency.toTileOpacity`). 값 변경은 zIndex 와
   * 같은 계약으로 레이어 재생성 없이 `setOpacity` 로만 반영된다(D5).
   *
   * @default 1
   */
  opacity?: number;
  /**
   * @desciprtion this is tile position values to be optimized.
   */
  tileMatrix?: TileMatrix;
  /**
   * true면 URL을 직접 XYZ 템플릿으로 사용 (TMS Y-flip 없음).
   * VWorld 위성 타일 등 표준 XYZ URL 사용 시 필요.
   * @default false
   */
  useDirectUrl?: boolean;
  /** 레이어 properties (예: { name: "base-vworld-satellite" }) */
  properties?: Record<string, any>;
}

export const TileLayer = ({
  url,
  zIndex = 0,
  maxZoom = 42,
  minZoom = 0,
  crossOrigin = "anonymous",
  errorTileUrl,
  opacity = 1,
  tileMatrix,
  useDirectUrl = false,
  properties,
}: TileLayerProps) => {
  const map = useMap();

  const tileRef = useRef<OlTileLayer<XYZ> | null>(null);

  // zIndex 변경은 레이어 재생성 없이 순서만 갱신한다 — 타일 네트워크 재요청 방지(D5).
  useEffect(() => {
    tileRef.current?.setZIndex(zIndex);
  }, [zIndex]);

  // opacity 변경도 같은 계약 — 레이어 재생성 없이 값만 갱신한다(D5).
  // 슬라이더 드래그로 프레임마다 바뀌어도 타일 재다운로드가 없어야 한다.
  useEffect(() => {
    tileRef.current?.setOpacity(opacity);
  }, [opacity]);

  useEffect(() => {
    // errorTileUrl 이 지정되면 로드 실패 타일을 대체 이미지로 폴백한다(D8).
    // 미지정 시 tileLoadFunction 을 건드리지 않아 기존 기본 로딩 동작을 그대로 보존한다.
    const tileLoadFunction = errorTileUrl
      ? (tile: Tile, src: string) => {
          const image = (tile as ImageTile).getImage() as HTMLImageElement;
          image.onerror = () => {
            image.onerror = null;
            image.src = errorTileUrl;
          };
          image.src = src;
        }
      : undefined;

    let customTmsSource: XYZ;

    if (useDirectUrl) {
      customTmsSource = new XYZ({
        url,
        maxZoom,
        minZoom,
        crossOrigin,
        ...(tileLoadFunction && { tileLoadFunction }),
      });
    } else {
      // TileUrl 인스턴스를 타일마다 새로 만들지 않도록 클로저 밖으로 hoist(D8).
      const tileUrl = new TileUrl(url);
      customTmsSource = new XYZ({
        maxZoom,
        minZoom,
        crossOrigin,
        ...(tileLoadFunction && { tileLoadFunction }),
        tileUrlFunction: (tileCoord) => {
          const z = tileCoord[0];
          const x = tileCoord[1];
          const y = Math.pow(2, z) - tileCoord[2] - 1;
          const tileImageUrl = tileUrl.getUrlFromPosition(z, x, y);

          if (!tileMatrix) return tileImageUrl;

          const boundary = tileMatrix?.[z];

          if (
            boundary &&
            x >= boundary.minX &&
            x <= boundary.maxX &&
            tileCoord[2] >= boundary.minY &&
            tileCoord[2] <= boundary.maxY
          ) {
            return tileImageUrl;
          }

          return;
        },
      });
    }

    const tileLayer = new OlTileLayer({
      source: customTmsSource,
      zIndex,
      // 생성 시점에도 opacity 를 실어 첫 프레임이 불투명으로 깜빡이지 않게 한다.
      opacity,
      // 콜드 캐시에서 저해상도 타일을 먼저 채워 첫 표시 체감 지연을 줄인다(D2).
      preload: 2,
      ...(properties && { properties }),
    });
    tileRef.current = tileLayer;
    map.addLayer(tileLayer);

    // 비동기로 add 된 타일 레이어의 렌더 스케줄 레이스 안전망(D1).
    // mount 직후 + 첫 타일 로드 시 1회성으로 updateSize()+render() 를 nudge 한다.
    // RAF 가드로 과도한 렌더를 막고, setState 를 유발하지 않아 무한 루프가 없다.
    let rafId: number | null = null;
    const scheduleNudge = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        map.updateSize();
        map.render();
      });
    };
    scheduleNudge();
    const tileLoadKey = customTmsSource.once("tileloadend", scheduleNudge);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      unByKey(tileLoadKey);
      map.removeLayer(tileLayer);
      tileRef.current = null;
    };
    // zIndex·opacity 는 전용 effect 에서 setZIndex/setOpacity 로 처리하므로
    // 재빌드 의존성에서 제외한다(D5) — deps 에 넣으면 XYZ 소스가 재생성되어
    // 값이 바뀔 때마다 전체 타일이 재다운로드된다.
  }, [
    map,
    errorTileUrl,
    maxZoom,
    minZoom,
    crossOrigin,
    url,
    tileMatrix,
    useDirectUrl,
    properties,
  ]);
  return null;
};
